import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, Form, Input, Select, message, Popconfirm, Space, Alert, Tag, Row, Col, Empty, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { divisionService, propertyService, assetService } from '@iso/services';
import LocationPicker, { formatLatLng, parseLatLng } from '@iso/components/LocationPicker/LocationPicker';

// The areas in the Select Harvests trial, offered as one-click adds.
const SUGGESTED_AREAS = ['Maintenance', 'Irrigation', 'Site Management', 'Stock pad & Processing'];

const userLabel = (user) => `${user.name || ''} ${user.surname || ''}`.trim() || user.email || `User #${user.id}`;

// Coordinates arrive from the API as strings (DECIMAL columns) — cast before use.
const toPosition = (lat, lng) => {
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  return Number.isFinite(la) && Number.isFinite(ln) ? formatLatLng(la, ln) : '';
};

const trimOrNull = (v) => {
  const t = (v || '').trim();
  return t === '' ? null : t;
};

// The responsible-person picker shows (and searches) "name - phone - role";
// the role is the user's permission on this property.
const userOptionLabel = (user) => [
  userLabel(user),
  trimOrNull(user.mobile_phone) || 'no phone',
  user.more_info && user.more_info.permission,
].filter(Boolean).join(' - ');

// One card's form values, from the division's site row at this property.
const siteToValues = (site) => ({
  contact_user_id: (site && site.contact_user_id) ? Number(site.contact_user_id) : undefined,
  contact_name: (site && site.contact_name) || '',
  contact_phone: (site && site.contact_phone) || '',
  facility_id: (site && site.facility_id) ? Number(site.facility_id) : undefined,
  position: site ? toPosition(site.latitude, site.longitude) : '',
  location_note: (site && site.location_note) || '',
});

// The `site` body the API takes, from one card's form values.
const valuesToSite = (v) => {
  const point = parseLatLng((v && v.position) || '');
  return {
    contact_user_id: (v && v.contact_user_id) || null,
    contact_name: trimOrNull(v && v.contact_name),
    contact_phone: trimOrNull(v && v.contact_phone),
    facility_id: (v && v.facility_id) || null,
    latitude: point ? point[0] : null,
    longitude: point ? point[1] : null,
    location_note: trimOrNull(v && v.location_note),
  };
};

const sameSite = (a, b) => JSON.stringify(valuesToSite(a)) === JSON.stringify(valuesToSite(b));

// simplification: a rough mobile check (9+ digits) for the status line only; the
// backend's strict E.164 check decides what is actually texted.
const looksLikeMobile = (phone) => String(phone || '').replace(/\D/g, '').length >= 9;

// Who is told when a contractor signs in for this area, in words.
const NotifyStatus = ({ values, setUp }) => {
  const v = values || {};
  const hasName = !!trimOrNull(v.contact_name);
  const hasUser = !!v.contact_user_id;
  const hasSms = looksLikeMobile(v.contact_phone);
  let type;
  let text;
  if (!hasName) {
    type = 'warning';
    text = setUp
      ? 'No responsible person — contractors who choose this area are sent to the main office.'
      : 'Not offered to contractors at this property yet — add a responsible person or hub.';
  } else if (hasSms && hasUser) {
    type = 'success';
    text = 'Notified by SMS and in the app when a contractor signs in for this area.';
  } else if (hasSms) {
    type = 'success';
    text = 'Notified by SMS when a contractor signs in. Pick a user as well to also notify them in the app.';
  } else if (hasUser) {
    type = 'info';
    text = 'Notified in the app only — add a mobile number to also send an SMS with a confirm link.';
  } else {
    type = 'warning';
    text = 'Shown to contractors, but nobody is notified — pick a user or add a mobile number.';
  }
  return <Alert type={type} showIcon message={text} style={{ marginTop: 4 }} />;
};

/**
 * Property Settings -> Divisions: the work areas contractors choose at QR
 * check-in, each with its responsible person and hub at this property. Laid
 * out like Safety Roles: one card per area, one Save.
 *
 * A division belongs to the property's corporate group (name shared across its
 * sites) or, for a property with no group, to the property alone. The person
 * and hub are this property's `site` row on the division.
 */
const DivisionsManagement = ({ propertyId, corporateGroupId, canManage }) => {
  const [divisions, setDivisions] = useState([]);
  // Saved values per division id, to tell which cards have changed.
  const [savedValues, setSavedValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(null); // name being added
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  // Cards whose exact-pin map is open.
  const [pinOpen, setPinOpen] = useState({});
  // null | { mode: 'add' } | { mode: 'rename', division }
  const [nameModal, setNameModal] = useState(null);
  const [form] = Form.useForm();
  const [nameForm] = Form.useForm();

  const loadDivisions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await divisionService.getListForProperty(propertyId, true);
      if (response && response.code === '0000') {
        const list = response.divisions || [];
        const values = {};
        list.forEach((d) => {
          values[d.id] = siteToValues(d.site);
        });
        setDivisions(list);
        setSavedValues(values);
        form.setFieldsValue({ areas: values });
      }
    } catch (error) {
      console.error('Error loading divisions:', error);
      message.error('Failed to load divisions');
    } finally {
      setLoading(false);
    }
  }, [propertyId, form]);

  const loadOptions = useCallback(() => {
    propertyService.getListUsersOfProperty(propertyId)
      .then((res) => {
        if (res && res.code === '0000') {
          setUsers(res.users || []);
        }
      })
      .catch((error) => console.error('Error loading property users:', error));

    assetService.getMapMarkers(propertyId)
      .then((res) => {
        if (res && res.code === '0000') {
          setFacilities(res.facilities || []);
        }
      })
      .catch((error) => console.error('Error loading map locations:', error));
  }, [propertyId]);

  useEffect(() => {
    if (propertyId) {
      loadDivisions();
      loadOptions();
    }
  }, [propertyId, loadDivisions, loadOptions]);

  const areas = Form.useWatch('areas', form) || {};
  const changedIds = divisions
    .map((d) => d.id)
    .filter((id) => areas[id] && savedValues[id] && !sameSite(areas[id], savedValues[id]));

  // Add a division without reloading, so unsaved edits on other cards survive.
  const appendDivision = (division, site) => {
    const values = siteToValues(site);
    setDivisions((prev) => [...prev, { ...division, site: site || null }]);
    setSavedValues((prev) => ({ ...prev, [division.id]: values }));
    form.setFieldsValue({ areas: { [division.id]: values } });
  };

  const createDivision = async (name, description) => {
    setAdding(name);
    try {
      // An empty `site` still sets the area up at this property (the backend
      // creates the site row for a division added from here).
      const response = await divisionService.storeOrUpdate({
        name,
        description: description || null,
        is_active: 1,
        property_id: propertyId,
        site: {},
      });
      if (response && response.code === '0000' && response.division) {
        appendDivision(response.division, response.site);
        message.success(`${name} added`);
        return true;
      }
      message.error((response && response.message) || 'Failed to add division');
    } catch (error) {
      console.error('Error adding division:', error);
      message.error('Failed to add division');
    } finally {
      setAdding(null);
    }
    return false;
  };

  const handleDelete = async (division) => {
    try {
      const response = await divisionService.destroy(division.id, propertyId);
      if (response && response.code === '0000') {
        message.success('Division deleted');
        setDivisions((prev) => prev.filter((d) => d.id !== division.id));
        setSavedValues((prev) => {
          const next = { ...prev };
          delete next[division.id];
          return next;
        });
      } else {
        message.error((response && response.message) || 'Failed to delete division');
      }
    } catch (error) {
      console.error('Error deleting division:', error);
      message.error('Failed to delete division');
    }
  };

  const openNameModal = (modal) => {
    nameForm.resetFields();
    if (modal.mode === 'rename') {
      nameForm.setFieldsValue({ name: modal.division.name, description: modal.division.description });
    }
    setNameModal(modal);
  };

  const submitName = async (values) => {
    const name = values.name.trim();
    const description = trimOrNull(values.description);
    if (nameModal.mode === 'add') {
      if (await createDivision(name, description)) {
        setNameModal(null);
      }
      return;
    }
    const division = nameModal.division;
    try {
      setSaving(true);
      // No `site` key: this card's person and hub stay as saved.
      const response = await divisionService.storeOrUpdate(
        { name, description, is_active: 1, property_id: propertyId },
        division.id
      );
      if (response && response.code === '0000') {
        setDivisions((prev) => prev.map((d) => (d.id === division.id ? { ...d, name, description, is_active: 1 } : d)));
        message.success('Division updated');
        setNameModal(null);
      } else {
        message.error((response && response.message) || 'Failed to update division');
      }
    } catch (error) {
      console.error('Error renaming division:', error);
      message.error('Failed to update division');
    } finally {
      setSaving(false);
    }
  };

  // Save every changed card, one request at a time; a failed card keeps its edits.
  const handleSaveAll = async () => {
    try {
      await form.validateFields();
    } catch (e) {
      message.error('Please fix the highlighted fields');
      return;
    }
    const values = form.getFieldValue('areas') || {};
    setSaving(true);
    const failed = [];
    const saved = {};
    const sites = {};
    for (const id of changedIds) {
      const division = divisions.find((d) => d.id === id);
      try {
        const response = await divisionService.storeOrUpdate({
          name: division.name,
          description: division.description || null,
          // As before: saving a division keeps (or puts) it active.
          is_active: 1,
          property_id: propertyId,
          site: valuesToSite(values[id]),
        }, id);
        if (response && response.code === '0000') {
          saved[id] = values[id];
          sites[id] = response.site || null;
        } else {
          failed.push(`${division.name}: ${(response && response.message) || 'not saved'}`);
        }
      } catch (error) {
        console.error('Error saving division:', error);
        failed.push(`${division.name}: not saved`);
      }
    }
    setSavedValues((prev) => ({ ...prev, ...saved }));
    setDivisions((prev) => prev.map((d) => (
      Object.prototype.hasOwnProperty.call(sites, d.id) ? { ...d, site: sites[d.id], is_active: 1 } : d
    )));
    setSaving(false);
    if (failed.length) {
      message.error(`Some areas were not saved: ${failed.join('; ')}`);
    } else {
      message.success('Divisions saved');
    }
  };

  // Picking a user pre-fills the person's details; both stay editable.
  const handleUserChange = (divisionId, userId) => {
    const user = users.find((u) => Number(u.id) === Number(userId));
    if (!user) {
      return;
    }
    const fill = { contact_name: userLabel(user) };
    if (user.mobile_phone) {
      fill.contact_phone = user.mobile_phone;
    }
    form.setFieldsValue({ areas: { [divisionId]: fill } });
  };

  if (!propertyId) {
    return (
      <Alert
        message="No Property Selected"
        description="Please select a property to manage divisions."
        type="warning"
        showIcon
      />
    );
  }

  const existingNames = divisions.map((d) => (d.name || '').trim().toLowerCase());
  const suggestions = SUGGESTED_AREAS.filter((n) => !existingNames.includes(n.toLowerCase()));
  const userOptions = users.map((u) => ({ value: Number(u.id), label: userOptionLabel(u) }));
  // map-markers returns ids as strings, saved sites as numbers — normalise.
  const facilityOptions = facilities.map((f) => ({ value: Number(f.id), label: f.title || `Location #${f.id}` }));

  return (
    <div>
      {corporateGroupId && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Area names are shared across your corporate group's properties. The responsible person and hub below are for this property only."
        />
      )}

      {canManage && (
        <Space wrap style={{ marginBottom: 16 }}>
          {suggestions.map((name) => (
            <Button
              key={name}
              icon={<PlusOutlined />}
              loading={adding === name}
              disabled={!!adding}
              onClick={() => createDivision(name)}
            >
              {name}
            </Button>
          ))}
          <Button type="dashed" icon={<PlusOutlined />} disabled={!!adding} onClick={() => openNameModal({ mode: 'add' })}>
            Add area
          </Button>
        </Space>
      )}

      <Spin spinning={loading}>
        {!loading && divisions.length === 0 && (
          <Empty description={canManage ? 'No work areas yet. Add one above.' : 'No work areas yet.'} />
        )}

        <Form form={form} layout="vertical" disabled={!canManage}>
          {divisions.map((division) => {
            const id = division.id;
            const v = areas[id] || {};
            const facility = facilities.find((f) => Number(f.id) === Number(v.facility_id));
            const referenceMarker = facility
              ? { latitude: facility.lat, longitude: facility.lng, label: facility.title }
              : null;
            return (
              <Card
                key={id}
                type="inner"
                size="small"
                style={{ marginBottom: 16 }}
                title={(
                  <Space wrap>
                    <span>{division.name}</span>
                    {Number(division.is_active) === 0 && <Tag color="red">Inactive</Tag>}
                    {/* Contractors here only see divisions with a site row at this property. */}
                    {!division.site && <Tag color="orange">Not set up at this site</Tag>}
                    {changedIds.includes(id) && <Tag color="blue">Unsaved</Tag>}
                  </Space>
                )}
                extra={canManage && (
                  <Space>
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => openNameModal({ mode: 'rename', division })}
                    >
                      Rename
                    </Button>
                    <Popconfirm
                      title={`Delete ${division.name}?${division.corporate_group_id ? ' It is removed from your corporate group too.' : ''} Only possible when no ${division.corporate_group_id ? 'other property or ' : ''}training session uses it.`}
                      onConfirm={() => handleDelete(division)}
                      okText="Delete"
                      cancelText="Cancel"
                    >
                      <Button type="link" size="small" danger icon={<DeleteOutlined />} aria-label={`Delete ${division.name}`} />
                    </Popconfirm>
                  </Space>
                )}
              >
                {division.description && <p style={{ color: '#666', marginTop: 0 }}>{division.description}</p>}
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item name={['areas', id, 'contact_user_id']} label="Responsible person">
                      <Select
                        showSearch
                        allowClear
                        placeholder="Select a user (name - phone - role)"
                        optionFilterProp="label"
                        options={userOptions}
                        onChange={(userId) => handleUserChange(id, userId)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name={['areas', id, 'contact_name']}
                      label="Name shown to contractors"
                      rules={[{ max: 255, message: 'Name must be 255 characters or fewer' }]}
                    >
                      <Input placeholder="e.g. Riley Fillmore" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name={['areas', id, 'contact_phone']}
                      label="Mobile (for SMS)"
                      rules={[{ max: 50, message: 'Phone must be 50 characters or fewer' }]}
                    >
                      <Input placeholder="e.g. 0412 345 678" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name={['areas', id, 'facility_id']} label="Hub (map location)">
                      <Select
                        showSearch
                        allowClear
                        placeholder="Pick a map location (optional)"
                        optionFilterProp="label"
                        options={facilityOptions}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={10}>
                    <Form.Item
                      name={['areas', id, 'location_note']}
                      label="Where to meet"
                      rules={[{ max: 255, message: 'Location note must be 255 characters or fewer' }]}
                    >
                      <Input placeholder="e.g. Workshop B, east door" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Exact pin">
                      <Button
                        icon={<EnvironmentOutlined />}
                        onClick={() => setPinOpen((prev) => ({ ...prev, [id]: !prev[id] }))}
                        aria-expanded={!!pinOpen[id]}
                      >
                        {pinOpen[id] ? 'Hide map' : (v.position ? 'Change pin' : 'Set exact pin')}
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
                {/* Always mounted (hidden when closed), so the pin stays in the form. */}
                <div hidden={!pinOpen[id]}>
                  <Form.Item
                    name={['areas', id, 'position']}
                    label="Exact position"
                    rules={[
                      {
                        validator: (_, value) => (!value || !value.trim() || parseLatLng(value)
                          ? Promise.resolve()
                          : Promise.reject(new Error('Enter coordinates as "lat, lng" or pick on the map'))),
                      },
                    ]}
                  >
                    {pinOpen[id] ? <LocationPicker showAreaSelect referenceMarker={referenceMarker} /> : <Input />}
                  </Form.Item>
                </div>
                <NotifyStatus values={v} setUp={!!division.site} />
              </Card>
            );
          })}
        </Form>
      </Spin>

      {canManage && divisions.length > 0 && (
        <div style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="large"
            loading={saving}
            disabled={changedIds.length === 0}
            onClick={handleSaveAll}
            style={{ backgroundColor: '#0b2a1b', borderColor: '#0b2a1b', color: '#fff' }}
          >
            {changedIds.length > 1 ? `Save ${changedIds.length} areas` : 'Save'}
          </Button>
        </div>
      )}

      <Modal
        title={nameModal && nameModal.mode === 'rename' ? 'Rename area' : 'Add area'}
        open={!!nameModal}
        onCancel={() => setNameModal(null)}
        footer={null}
        destroyOnClose
      >
        <Form form={nameForm} layout="vertical" onFinish={submitName}>
          <Form.Item
            name="name"
            label="Area name"
            rules={[
              { required: true, whitespace: true, message: 'Please enter an area name' },
              { max: 255, message: 'Area name must be 255 characters or fewer' },
            ]}
          >
            <Input placeholder="e.g. Maintenance, Irrigation, Site Management" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
          {nameModal && nameModal.mode === 'rename' && nameModal.division.corporate_group_id && (
            <Alert type="info" showIcon style={{ marginBottom: 16 }} message="The new name applies to every property in your corporate group." />
          )}
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setNameModal(null)}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving || !!adding}
                style={{ backgroundColor: '#0b2a1b', borderColor: '#0b2a1b' }}
              >
                {nameModal && nameModal.mode === 'rename' ? 'Save' : 'Add'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DivisionsManagement;
